/**
 * RaceResultSummary — render read-only del resultado guardado para una
 * race con `completed=true`. Renderizado en la sección "Resultados
 * guardados" de la pestaña Resultados (v16.3.1 R.1 + v16.4.1 §T4).
 *
 * Decisión scope R.1: SOLO read-only. La edición de un resultado
 * guardado queda fuera de scope (apuntada para R.3 / backlog) — el
 * atleta cubre el "rehacer" borrando + creando una nueva race (B.4)
 * o desde el día de carrera siguiente.
 *
 * El componente oculta campos null. El backend B.1 acepta cualquier
 * subset de actual_* (todos opcionales) → un atleta puede guardar SOLO
 * tiempo, o solo sensaciones, etc.
 */
import { distanceLabel } from "@/components/races/distanceLabel";
import { t } from "@/i18n/es";
import type { RaceTarget, Weather } from "@/types/api";

export interface RaceResultSummaryProps {
  race: RaceTarget;
}

function _formatRaceDate(iso: string): string {
  // Patrón anclado del repo: `T00:00:00` fuerza interpretación local.
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Formatea segundos como `H:MM:SS` (si >= 3600) o `MM:SS` (si <3600).
 *  Simétrico con el parser `_parseTimeToSeconds` de RaceResultModal. */
function _formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const totalSec = Math.round(seconds);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

const WEATHER_LABEL_KEY: Record<Weather, keyof typeof t.races> = {
  calor: "resultWeatherCalor",
  frio: "resultWeatherFrio",
  viento: "resultWeatherViento",
  lluvia: "resultWeatherLluvia",
  normal: "resultWeatherNormal",
};

function _weatherLabel(w: Weather): string {
  return t.races[WEATHER_LABEL_KEY[w]];
}

const FEELING_LABELS: Record<number, string> = {
  1: t.plan.feelingMal,
  2: t.plan.feelingRegular,
  3: t.plan.feelingBien,
  4: t.plan.feelingBien,
  5: t.plan.feelingMuyBien,
};

function _feelingLabel(feeling: number): string {
  return FEELING_LABELS[feeling] ?? String(feeling);
}

interface FieldRowProps {
  label: string;
  value: string;
  testId: string;
}

function FieldRow({ label, value, testId }: FieldRowProps) {
  return (
    <div
      data-testid={testId}
      className="flex items-baseline justify-between gap-2"
    >
      <span className="text-xs text-bone-muted">{label}</span>
      <span className="text-sm text-bone">{value}</span>
    </div>
  );
}

export function RaceResultSummary({ race }: RaceResultSummaryProps) {
  const name = race.name ?? distanceLabel(race.distance);
  // Recogemos los campos no-null en orden de relevancia para el atleta.
  const rows: FieldRowProps[] = [];
  if (race.actual_time_seconds != null) {
    rows.push({
      label: t.races.resultTimeLabel,
      value: _formatTime(race.actual_time_seconds),
      testId: `race-result-summary-time-${race.id}`,
    });
  }
  if (race.actual_distance_km != null) {
    rows.push({
      label: t.races.resultDistanceLabel,
      value: `${race.actual_distance_km} km`,
      testId: `race-result-summary-distance-${race.id}`,
    });
  }
  if (race.actual_avg_hr != null) {
    rows.push({
      label: t.races.resultAvgHrLabel,
      value: `${race.actual_avg_hr} ppm`,
      testId: `race-result-summary-avghr-${race.id}`,
    });
  }
  if (race.actual_max_hr != null) {
    rows.push({
      label: t.races.resultMaxHrLabel,
      value: `${race.actual_max_hr} ppm`,
      testId: `race-result-summary-maxhr-${race.id}`,
    });
  }
  if (race.actual_feeling != null) {
    rows.push({
      label: t.races.resultFeelingLabel,
      value: `${race.actual_feeling} · ${_feelingLabel(race.actual_feeling)}`,
      testId: `race-result-summary-feeling-${race.id}`,
    });
  }
  if (race.actual_weather != null) {
    rows.push({
      label: t.races.resultWeatherLabel,
      value: _weatherLabel(race.actual_weather),
      testId: `race-result-summary-weather-${race.id}`,
    });
  }

  return (
    <article
      data-testid={`race-result-summary-${race.id}`}
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-carbon-raised p-4"
    >
      <header className="flex flex-col gap-1">
        <p className="text-sm font-medium text-bone">{name}</p>
        <p className="text-xs text-bone-muted">
          {_formatRaceDate(race.race_date)} · {distanceLabel(race.distance)}
        </p>
      </header>
      {rows.length > 0 && (
        <dl className="flex flex-col gap-1.5">
          {rows.map((row) => (
            <FieldRow key={row.testId} {...row} />
          ))}
        </dl>
      )}
    </article>
  );
}
